import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { EyeIcon } from "@/shared/icons";
import useDesignHandlers from "@/domains/estate/hooks/handlers/useDesignHandlers";

type Props = {
  name: string;
  price: number;
  time?: number;
  isPaused: boolean;
};

export default function EstatateDesignVariantCounter({ name, price, isPaused, time = 60 }: Props) {
  const progress = useMotionValue(0);
  const [displayedTime, setDisplayedTime] = useState(time);
  const [remainingTime, setRemainingTime] = useState(time);
  const { closePreviewHandler } = useDesignHandlers();
  const { t } = useTranslation("estate");

  const radius = 22.75;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = useTransform(progress, [0, time], [0, circumference]);
  const displayTime = useTransform(progress, [0, time], [time, 0], {
    clamp: false,
  });

  useEffect(() => {
    if (displayedTime === 0) {
      closePreviewHandler();
    }
  }, [closePreviewHandler, displayedTime]);

  useEffect(() => {
    const unsubscribe = displayTime.on("change", (val) => {
      const newTime = Math.max(0, Math.ceil(val));
      setDisplayedTime(newTime);
      if (!isPaused) {
        setRemainingTime(newTime);
      }
    });

    let animation: ReturnType<typeof animate>;

    if (!isPaused) {
      progress.set(time - remainingTime);
      animation = animate(progress, time, {
        duration: remainingTime,
        ease: "linear",
      });
    }

    return () => {
      animation?.stop();
      unsubscribe();
    };
  }, [isPaused, displayTime, progress, remainingTime, time]);

  return (
    <div
      className="flex items-center justify-between min-w-[23rem] h-[6.125rem] py-3.5 px-5 rounded-[0.75rem]"
      style={{
        background: `linear-gradient(101deg, rgba(215, 52, 52, 0.20) 0%, rgba(18, 18, 18, 0.10) 21.28%, rgba(18, 18, 18, 0.00) 42.57%), #121212`,
        backgroundColor: "#121212",
      }}
    >
      <div className="">
        <p className="mb-0.5 mr-2 text-16 font-bold leading-none">
          {t(`estate.design.designVariants.${name}.title`)}
        </p>
        <p className="mb-1.5 text-16 font-bold text-accent-solid">{formatPrice(price)} ₽</p>
        <div className="flex items-center gap-1.5">
          <EyeIcon size={16} className="text-white/70" />
          <p className="text-15 font-medium leading-none text-white/50">
            {t("estate.design.testPreview")}
          </p>
        </div>
      </div>

      <div className="relative w-12 h-12">
        <svg className="w-full h-full" viewBox="0 0 48 48">
          <circle
            cx="24"
            cy="24"
            r={radius}
            fill="none"
            stroke="rgba(42, 42, 42, 0.50)"
            strokeWidth="2.5"
          />
        </svg>

        <motion.svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 48 48">
          <motion.circle
            cx="24"
            cy="24"
            r={radius}
            fill="none"
            stroke="#D73434"
            strokeWidth="2.5"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform="rotate(-90 24 24)"
          />
        </motion.svg>

        <div className="absolute top-0 left-0 flex items-center justify-center size-full">
          <p className="text-16 font-bold">{displayedTime}</p>
        </div>
      </div>
    </div>
  );
}
